import UploadOutlined from '@ant-design/icons/UploadOutlined';
import GiftOutlined from '@ant-design/icons/GiftOutlined';
import Button from 'antd/lib/button';
import DatePicker from 'antd/lib/date-picker';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import message from 'antd/lib/message';
import Model from 'antd/lib/modal';
import Select from 'antd/lib/select';
import Upload from 'antd/lib/upload';
import React, { useContext, useEffect, useState } from 'react';
import { axiosGetCall, axiosPostCall } from '../../api/axiosCall';
import { API_END_POINT, SOMETHING_WENT_WRONG } from '../../constant/constants';
import { AuthContext } from '../../context/AuthProvider';
import { uploadFile, S3ImageFolder } from '../../utils/imageUploader';
import moment from 'moment';

const { Option } = Select;

function AddPromotionalBanner({ isOpen, handleModelClose, callback }) {
  const [addForm] = Form.useForm();
  const { token, user } = useContext(AuthContext);

  const [fileList, setFileList] = useState([]);
  const [productList, setProductList] = useState([]);

  useEffect(() => {
    getProductList();
  }, []);

  async function getProductList() {
    try {
      const res = await axiosGetCall({
        apiEndPoint: API_END_POINT.GET_PRODUCT_OPTIONS,
        header: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.hideErrorFromUser) {
        setProductList(res.data);
      } else {
        message.error(res.error.message);
      }
    } catch (error) {
      message.error(SOMETHING_WENT_WRONG);
    }
  }

  const handleClose = () => {
    handleModelClose();
    setFileList([]);
    addForm.resetFields();
  };

  async function addPromotionalBanner({ fileURL }) {
    try {
      const formValues = addForm.getFieldValue();
      const res = await axiosPostCall({
        apiEndPoint: API_END_POINT.ADD_PROMOTIONAL_BANNER,
        header: {
          Authorization: `Bearer ${token}`,
        },
        body: {
          promotionalBannerName: formValues.name,
          promotionalBannerImage: fileURL,
          startDate: formValues.startDate.toISOString(),
          endDate: formValues.endDate.toISOString(),
          productId: formValues.productId,
          createdBy: user?.fullname || 'Admin',
        },
      });
      if (res?.hideErrorFromUser) {
        message.success(res.data.message);
        callback();
        handleClose();
        return;
      } else {
        message.error(res?.error?.message);
      }
    } catch (error) {
      message.error(SOMETHING_WENT_WRONG);
    }
  }

  const normFile = (e) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList;
  };

  async function handleAddPromotionalBanner() {
    try {
      const list = [];
      for (const file of fileList) {
        const data = await uploadFile(
          file,
          S3ImageFolder.PromotionalBanner,
          token,
        );
        list.push(data);
      }
      await addPromotionalBanner({ fileURL: list[0] });
    } catch (error) {
      console.error('uploadFile', error);
      return false;
    }
  }

  const uploadProps = {
    onRemove: (file) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
    beforeUpload: (file) => {
      if (fileList.length < 1) {
        setFileList([...fileList, file]);
      } else {
        message.error('Max image upload limit reached');
      }
      return false;
    },
    fileList,
  };

  return (
    <div>
      <Model
        open={isOpen}
        onCancel={handleClose}
        closable={false}
        footer={null}
      >
        <div className='px-6 pb-4'>
          <div className='flex items-center gap-3 mb-4'>
            <div className='p-2 rounded-lg shadow bg-gradient-to-r from-blue-500 to-purple-600'>
              <GiftOutlined className='text-white text-lg' />
            </div>
            <h2 className='text-xl font-bold text-text m-0'>
              Add Promotional Banner
            </h2>
          </div>

          <div className='bg-surface border border-border rounded-xl shadow p-4'>
            <Form
              form={addForm}
              layout='vertical'
            >
              <Form.Item
                label='Banner Name'
                name='name'
                rules={[
                  { required: true, message: 'Please enter banner name!' },
                ]}
              >
                <Input
                  name='promotionalBannerName'
                  placeholder='Enter promotional banner name'
                />
              </Form.Item>

              <Form.Item
                label='Product'
                name='productId'
                rules={[
                  { required: true, message: 'Please select a product!' },
                ]}
              >
                <Select
                  placeholder='Select a Product'
                  showSearch
                  optionFilterProp='children'
                >
                  {productList.map((product) => (
                    <Option
                      key={product.productId}
                      value={product.productId}
                    >
                      {product.productName}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label='Start Date'
                name='startDate'
                rules={[
                  { required: true, message: 'Please select start date!' },
                ]}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  placeholder='Select start date'
                  format='YYYY-MM-DD'
                  disabledDate={(current) =>
                    current && current < moment().startOf('day')
                  }
                />
              </Form.Item>

              <Form.Item
                label='End Date'
                name='endDate'
                rules={[{ required: true, message: 'Please select end date!' }]}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  placeholder='Select end date'
                  format='YYYY-MM-DD'
                  disabledDate={(current) => {
                    const startDate = addForm.getFieldValue('startDate');
                    return (
                      current &&
                      (current < moment().startOf('day') ||
                        (startDate && current < startDate))
                    );
                  }}
                />
              </Form.Item>

              <Form.Item
                label='Upload Banner Image'
                valuePropName='fileList'
                getValueFromEvent={normFile}
                rules={[
                  { required: true, message: 'Please upload banner image!' },
                ]}
              >
                <Upload
                  accept='image/*'
                  {...uploadProps}
                  multiple={false}
                  maxCount={1}
                >
                  <Button
                    type='dashed'
                    icon={<UploadOutlined />}
                    className='border-primary text-primary'
                  >
                    Click to Upload
                  </Button>
                </Upload>
              </Form.Item>
            </Form>

            <div className='flex justify-end gap-3 pt-2'>
              <Button
                onClick={handleClose}
                className='w-28 bg-surface border border-border text-text hover:text-primary hover:border-primary'
              >
                Close
              </Button>
              <Button
                type='primary'
                onClick={handleAddPromotionalBanner}
                className='w-32 border-none bg-gradient-to-r from-blue-500 to-purple-600 shadow hover:shadow-md'
              >
                Add Banner
              </Button>
            </div>
          </div>
        </div>
      </Model>
    </div>
  );
}

export default AddPromotionalBanner;
